import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Creates axios config with authentication
 * @param {AbortSignal|null} signal - Optional abort signal
 * @returns {Object} - Axios config object
 */
const getAuthConfig = (signal = null) => {
  const token = localStorage.getItem('token');
  const config = {
    withCredentials: true // Ensure cookies are sent
  };
  if (token) {
    config.headers = { Authorization: `Bearer ${token}` };
  }
  if (signal) {
    config.signal = signal;
  }
  return config;
};

/**
 * Fetches paginated chats (metadata-only) for the current user.
 * By default, returns the first page to keep dashboard load fast.
 *
 * @param {AbortSignal|null} signal - Optional abort signal for cancellation
 * @param {Object} options - Pagination options
 * @param {number} options.page - Page number (1-based)
 * @param {number} options.limit - Page size (default 8)
 * @returns {Promise<{ chats: Array, page: number, limit: number, total: number, totalPages: number, hasMore: boolean }>}
 */
export const fetchChats = async (signal = null, { page = 1, limit = 8 } = {}) => {
  try {
    const config = getAuthConfig(signal);
    const response = await axios.get(`${API_URL}/ai/chats`, {
      ...config,
      params: { page, limit },
    });

    const {
      chats: chatsData = [],
      page: currentPage = page,
      limit: pageSize = limit,
      total = chatsData.length,
      totalPages = 1,
      hasMore = false,
    } = response.data || {};

    // IMPORTANT: Only use chatId if it's 16 digits, otherwise use _id (for old chats)
    const chatsWithIds = chatsData.map(chat => {
      const chatId = chat.chatId || chat._id;
      if (chatId && /^\d{16}$/.test(String(chatId))) {
        chat.chatId16 = chat.chatId16 || chatId;
      } else {
        // Old format ID - we'll use it as-is but log a warning
        console.warn(`Chat ${chatId} has non-16-digit ID, using as-is. New chats will have 16-digit IDs.`);
        chat.chatId16 = chat.chatId16 || chatId;
      }
      return chat;
    });

    return {
      chats: chatsWithIds,
      page: currentPage,
      limit: pageSize,
      total,
      totalPages,
      hasMore,
    };
  } catch (error) {
    if (error.response?.status === 401 || error.name === 'AbortError' || error.name === 'CanceledError') {
      return {
        chats: [],
        page: 1,
        limit,
        total: 0,
        totalPages: 1,
        hasMore: false,
      };
    }
    console.error('Error fetching chats:', error);
    throw error;
  }
};

/**
 * Loads a single chat by ID
 * @param {string} chatId - The chat ID to load
 * @returns {Promise<Object>} - Chat object with all data
 */
export const loadChat = async (chatId) => {
  if (!chatId || typeof chatId !== 'string' || chatId.trim() === '') {
    throw new Error('Invalid chat ID');
  }

  try {
    const config = getAuthConfig();
    const response = await axios.get(`${API_URL}/ai/chats/${encodeURIComponent(chatId)}`, config);
    return response.data.chat;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized');
    }
    if (error.response?.status === 404) {
      throw new Error('Chat not found');
    }
    if (error.response?.status === 500) {
      throw new Error('Server error loading chat');
    }
    console.error('Error loading chat:', error);
    throw error;
  }
};

/**
 * Updates a chat (title, selectedModels, responses, etc.)
 * @param {string} chatId - The chat ID to update
 * @param {Object} updateData - Data to update (title, selectedModels, responses, etc.)
 * @returns {Promise<Object>} - Updated chat object
 */
export const updateChat = async (chatId, updateData) => {
  if (!chatId || typeof chatId !== 'string' || chatId.trim() === '') {
    throw new Error('Invalid chat ID');
  }

  try {
    const config = getAuthConfig();
    const response = await axios.put(
      `${API_URL}/ai/chats/${chatId}`,
      updateData,
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized');
    }
    if (error.response?.status === 404) {
      throw new Error('Chat not found');
    }
    console.error('Error updating chat:', error);
    throw error;
  }
};

/**
 * Deletes a chat
 * @param {string} chatId - The chat ID to delete
 * @returns {Promise<void>}
 */
export const deleteChat = async (chatId) => {
  if (!chatId || typeof chatId !== 'string' || chatId.trim() === '') {
    throw new Error('Invalid chat ID');
  }

  try {
    const config = getAuthConfig();
    await axios.delete(`${API_URL}/ai/chats/${chatId}`, config);
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized');
    }
    if (error.response?.status === 404) {
      throw new Error('Chat not found');
    }
    console.error('Error deleting chat:', error);
    throw error;
  }
};

/**
 * Polls for chat updates (used during streaming)
 * @param {string} chatId - The chat ID to poll
 * @returns {Promise<Object>} - Chat object with updated data
 */
export const pollChat = async (chatId) => {
  if (!chatId || typeof chatId !== 'string' || chatId.trim() === '') {
    throw new Error('Invalid chat ID');
  }

  try {
    const token = localStorage.getItem('token');
    const config = {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true
    };
    const response = await axios.get(`${API_URL}/ai/chats/${encodeURIComponent(chatId)}`, config);
    return response.data.chat;
  } catch (error) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized');
    }
    if (error.response?.status === 404) {
      throw new Error('Chat not found');
    }
    console.warn('Polling error:', error);
    throw error;
  }
};

